/**
 * Brand billing home: settled campaign payments and receipt download.
 *
 * There is still no general wallet. History is every card payment that
 * settled onto a campaign. A receipt is a payment receipt, not a tax invoice.
 */
import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Eyebrow, LinkButton, Money, PageHeader, StateBlock } from "@ui/primitives";
import { FormError } from "@ui/forms";
import { fromMinorString, type CurrencyCode } from "@contracts/money";
import { fontSize, space } from "@design/index";
import { api } from "@app/shared/api";

export interface BillingPayment {
  id: string;
  campaign_id: string;
  campaign_name: string;
  currency: string;
  gross_minor: string;
  net_campaign_minor: string;
  fee_minor: string;
  tax_minor: string;
  allocated: boolean;
  allocated_at: string;
  receipt_number: string;
  status: string;
}

const Amount = ({ minor, currency, emphasis = false }: {
  minor: string; currency: string; emphasis?: boolean;
}) => <Money value={fromMinorString(minor, currency as CurrencyCode)} emphasis={emphasis} />;

function paidOn(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

async function downloadReceipt(id: string): Promise<string | null> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/billing/payments/${id}/receipt`, {
      credentials: "same-origin",
      headers: { "x-cm-request": "1" },
    });
  } catch {
    return "Could not reach the server. Check your connection.";
  }
  if (!res.ok) {
    const payload = await res.json().catch(() => ({})) as { error?: { message?: string } };
    return payload.error?.message ?? "The receipt could not be downloaded.";
  }
  const blob = await res.blob();
  const header = res.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(header);
  const name = match?.[1] ?? `receipt-${id}.html`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return null;
}

export function BillingHome() {
  const [payments, setPayments] = useState<BillingPayment[] | null>(null);
  const [canDownload, setCanDownload] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await api.get<{ payments: BillingPayment[]; can_download: boolean }>("/billing/payments");
    if (!r.ok) {
      setError(r.message);
      setPayments([]);
      return;
    }
    setError(null);
    setPayments(r.data.payments);
    setCanDownload(Boolean(r.data.can_download));
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function onDownload(id: string) {
    setBusyId(id);
    const fail = await downloadReceipt(id);
    setBusyId(null);
    if (fail) setError(fail);
  }

  return (
    <>
      <PageHeader eyebrow="Billing" title="Campaign funding"
        description="Payments that settled onto a campaign. There is no general brand wallet - you fund a specific brief after the invoice address is complete."
        actions={<LinkButton href="/campaigns" variant="primary" size="sm">Open campaigns</LinkButton>} />

      {error ? <FormError message={error} /> : null}

      {payments === null ? (
        <StateBlock kind="loading" title="Loading payments" />
      ) : payments.length === 0 ? (
        <StateBlock kind="empty" title="No payments yet"
          detail="Open an approved campaign and pay by card. Settled payments appear here, and you can download a receipt for each one."
          action={<LinkButton href="/campaigns" variant="primary" size="sm">Open campaigns</LinkButton>} />
      ) : (
        <Card padding={0} style={{ overflow: "hidden" }}>
          <div style={{ padding: "16px 18px", borderBottom: "1px solid var(--line)",
                        display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
            <Eyebrow>Transaction history</Eyebrow>
            <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>
              {payments.length} settled
            </span>
          </div>
          <div style={{ display: "grid" }}>
            {payments.map((p) => (
              <div key={p.id} style={{
                display: "flex",
                flexWrap: "wrap",
                gap: space(3),
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 18px",
                borderBottom: "1px solid var(--line)",
              }}>
                <div style={{ display: "grid", gap: 4, minWidth: 0, flex: "1 1 220px" }}>
                  <div style={{ fontWeight: 650, fontSize: fontSize.bodyLg }}>{p.campaign_name}</div>
                  <div style={{ fontSize: fontSize.label, color: "var(--muted)" }}>
                    {paidOn(p.allocated_at)} · {p.receipt_number}
                  </div>
                </div>
                <div style={{ display: "grid", gap: 4, justifyItems: "end" }}>
                  <Amount minor={p.gross_minor} currency={p.currency} emphasis />
                  <span style={{ fontSize: fontSize.micro, color: "var(--faint)",
                                 display: "inline-flex", gap: 6, alignItems: "baseline" }}>
                    Pool <Amount minor={p.net_campaign_minor} currency={p.currency} />
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Badge tone="ok">Settled</Badge>
                  {canDownload ? (
                    <Button variant="secondary" size="sm"
                      disabled={busyId === p.id}
                      onClick={() => void onDownload(p.id)}>
                      {busyId === p.id ? "Downloading…" : "Download receipt"}
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}
